// src/components/customer/CustomerInfoPanel.js
import React, { useEffect, useMemo, useState } from "react";
import {
  BadgeCheck,
  Building2,
  Mail,
  MapPin,
  Pencil,
  Phone,
  Plus,
  ShieldCheck,
  Star,
  Store,
  UserRound,
  X,
} from "lucide-react";

const getAccountId = (a) => {
  const id = a?.id || a?.accountId || a?._id || "";
  return (id || "").toString().trim();
};

const getAccountDisplayName = (a) => {
  const raw =
    a?.name ||
    a?.businessName ||
    a?.accountName ||
    a?.locationName ||
    a?.storeName ||
    a?.companyName ||
    a?.Title ||
    a?.title ||
    "";

  const name = (raw || "").toString().trim();
  if (name) return name;

  const city = a?.addressCity || a?.city || a?.address?.city || "";
  const state = a?.addressState || a?.state || a?.address?.state || "";
  const fallback = [city, state].filter(Boolean).join(", ").trim();
  return fallback || "—";
};

const getAccountAssignedRep = (account) => {
  if (!account) return null;
  const nestedRep =
    account?.assignedTo ||
    account?.assignedUser ||
    account?.repUser ||
    account?.salesRep ||
    null;

  return {
    id:
      account?.assignedToId ||
      account?.assignedUserId ||
      account?.repUserId ||
      account?.salesRepId ||
      nestedRep?.id ||
      nestedRep?.uid ||
      nestedRep?.userId ||
      null,
    fullName:
      account?.assignedToName ||
      account?.repUserName ||
      account?.salesRepName ||
      nestedRep?.fullName ||
      nestedRep?.displayName ||
      nestedRep?.name ||
      "",
    email:
      account?.assignedToEmail ||
      account?.repUserEmail ||
      account?.salesRepEmail ||
      nestedRep?.email ||
      "",
    phone:
      account?.assignedToPhone ||
      account?.repUserPhone ||
      account?.salesRepPhone ||
      nestedRep?.phone ||
      nestedRep?.mobilePhone ||
      "",
  };
};

function formatAddress(a) {
  const addr = a?.address || {};

  const street =
    a?.addressStreet ||
    a?.street ||
    a?.addressLine1 ||
    addr?.line1 ||
    addr?.address1 ||
    addr?.addressLine1 ||
    "";

  const street2 =
    a?.addressStreet2 ||
    a?.street2 ||
    a?.addressLine2 ||
    a?.addressLineTwo ||
    addr?.line2 ||
    addr?.address2 ||
    addr?.addressLine2 ||
    "";

  const city = a?.addressCity || a?.city || addr?.city || a?.addressCityName || "";
  const stateRaw = a?.addressState || a?.state || addr?.state || "";
  const state = (stateRaw || "").toString().trim().toUpperCase();
  const zip = a?.addressZip || a?.zip || addr?.zip || a?.postalCode || addr?.postalCode || "";

  const parts = [
    (street || "").toString().trim(),
    (street2 || "").toString().trim(),
    [city && city.trim(), state].filter(Boolean).join(", "),
    (zip || "").toString().trim(),
  ].filter(Boolean);

  return parts.length ? parts.join(" • ") : "—";
}

const blankLocationEditor = {
  name: "",
  street: "",
  city: "",
  state: "",
  zip: "",
  phone: "",
};

const toLocationDraft = (account) => ({
  name: getAccountDisplayName(account) === "—" ? "" : getAccountDisplayName(account),
  street:
    account?.addressStreet ||
    account?.street ||
    account?.addressLine1 ||
    account?.address?.addressLine1 ||
    account?.address?.line1 ||
    "",
  city: account?.addressCity || account?.city || account?.address?.city || "",
  state: account?.addressState || account?.state || account?.address?.state || "",
  zip: account?.addressZip || account?.zip || account?.address?.zip || "",
  phone: account?.phone || account?.primaryContact?.phone || "",
});

const DetailRow = ({ icon: Icon, label, value, href }) => {
  if (!value) return null;
  const content = href ? (
    <a href={href} className="text-slate-700 hover:text-blue-700 hover:underline">
      {value}
    </a>
  ) : (
    <span className="text-slate-700">{value}</span>
  );

  return (
    <div className="flex items-start gap-2 text-xs">
      <Icon className="mt-0.5 h-3.5 w-3.5 text-slate-400" />
      <div className="min-w-0">
        <div className="font-medium text-slate-500">{label}</div>
        <div className="break-words">{content}</div>
      </div>
    </div>
  );
};

const CustomerInfoPanel = ({
  customerName,
  customerEmail,
  isVerified,
  verifiedOnLabel,
  onSendVerification,
  verificationSending,
  verificationMessage,
  company,
  repUser,
  myAccount,
  onLogout,
  accounts = [],
  primaryAccountId = "",
  onSetPrimaryLocation,
  settingPrimary = false,
  showAddLocation,
  setShowAddLocation,
  newLocation,
  setNewLocation,
  addLocationSaving,
  onAddLocationSubmit,
  profileForm,
  setProfileForm,
  isEditing,
  savingProfile,
  onEditProfile,
  onCancelProfile,
  onSaveProfile,
  onOpenLegal,
  onSaveLocation,
  locationSavingId = "",
  initialView = "profile",
}) => {
  const [activeView, setActiveView] = useState(initialView === "company" ? "company" : "profile");
  const [editingLocationId, setEditingLocationId] = useState("");
  const [locationDraft, setLocationDraft] = useState(blankLocationEditor);

  const primaryLocation = useMemo(() => {
    const list = Array.isArray(accounts) ? accounts : [];
    const one = list.length === 1 ? list[0] : null;

    if (primaryAccountId) {
      const byId =
        list.find((a) => getAccountId(a) === primaryAccountId) ||
        (getAccountId(myAccount) === primaryAccountId ? myAccount : null);
      if (byId) return byId;
    }

    if (one) return one;
    return myAccount || list[0] || null;
  }, [accounts, primaryAccountId, myAccount]);

  const effectivePrimaryId = useMemo(() => {
    return (
      getAccountId(primaryLocation) ||
      (primaryAccountId || "").toString().trim() ||
      getAccountId(myAccount) ||
      ""
    );
  }, [primaryLocation, primaryAccountId, myAccount]);

  const orderedLocations = useMemo(() => {
    const list = Array.isArray(accounts) ? accounts : [];
    return [...list].sort((a, b) => {
      const aPrimary = getAccountId(a) === effectivePrimaryId ? 1 : 0;
      const bPrimary = getAccountId(b) === effectivePrimaryId ? 1 : 0;
      if (aPrimary !== bPrimary) return bPrimary - aPrimary;
      return getAccountDisplayName(a).localeCompare(getAccountDisplayName(b));
    });
  }, [accounts, effectivePrimaryId]);

  const accountRep = useMemo(
    () => getAccountAssignedRep(primaryLocation || myAccount),
    [primaryLocation, myAccount]
  );

  const displayRep = repUser || accountRep;
  const repInitial = (displayRep?.fullName || displayRep?.displayName || "")
    .trim()
    .charAt(0)
    .toUpperCase();

  const primaryLocationName = getAccountDisplayName(primaryLocation || myAccount);
  const companyName = company?.businessName || company?.name || "Your distributor";
  const companyDescription =
    company?.description ||
    company?.tagline ||
    "Your distributor profile, contact details, and assigned support live here.";

  const launchLocationEditor = (account) => {
    setEditingLocationId(getAccountId(account));
    setLocationDraft(toLocationDraft(account));
  };

  const cancelLocationEditor = () => {
    setEditingLocationId("");
    setLocationDraft(blankLocationEditor);
  };

  const handleLocationSave = async () => {
    if (!editingLocationId || typeof onSaveLocation !== "function") return;
    try {
      await onSaveLocation(editingLocationId, locationDraft);
      cancelLocationEditor();
    } catch (err) {
      console.error("[CustomerInfoPanel] save location error:", err);
    }
  };

  useEffect(() => {
    setActiveView(initialView === "company" ? "company" : "profile");
  }, [initialView]);

  return (
    <div className="w-full">
      <div className="mx-auto max-w-xl space-y-3">
        <div className="overflow-visible rounded-3xl border border-slate-200 bg-white shadow-sm sm:overflow-hidden">
          <div className="border-b border-slate-200 bg-gradient-to-br from-slate-900 via-slate-850 to-slate-800 px-4 py-4 text-white">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h2 className="truncate text-lg font-semibold">
                    {customerName || "Customer"}
                  </h2>
                  {isVerified ? (
                    <ShieldCheck className="h-4 w-4 text-emerald-300" />
                  ) : null}
                </div>
                <p className="mt-1 truncate text-sm text-slate-200">{customerEmail || "—"}</p>
                {isVerified && verifiedOnLabel ? (
                  <p className="mt-1 text-[11px] text-slate-300">Verified {verifiedOnLabel}</p>
                ) : null}
              </div>

              <button
                type="button"
                onClick={onLogout}
                className="rounded-xl bg-white/10 px-3 py-2 text-xs font-semibold text-white transition hover:bg-white/20"
              >
                Log out
              </button>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setActiveView("profile")}
                className={`rounded-2xl px-3 py-2 text-sm font-medium transition ${
                  activeView === "profile"
                    ? "bg-white text-slate-900"
                    : "bg-white/10 text-white hover:bg-white/15"
                }`}
              >
                Profile
              </button>
              <button
                type="button"
                onClick={() => setActiveView("company")}
                className={`rounded-2xl px-3 py-2 text-sm font-medium transition ${
                  activeView === "company"
                    ? "bg-white text-slate-900"
                    : "bg-white/10 text-white hover:bg-white/15"
                }`}
              >
                Company
              </button>
            </div>
          </div>

          <div className="space-y-3 p-3 sm:p-4">
            {!isVerified ? (
              <div className="rounded-2xl border border-amber-200 bg-amber-50 px-3 py-3">
                <p className="text-sm font-semibold text-amber-900">
                  Verify your email to submit sample orders.
                </p>
                <button
                  type="button"
                  onClick={onSendVerification}
                  disabled={verificationSending}
                  className="mt-3 inline-flex items-center gap-2 rounded-xl bg-amber-600 px-3 py-2 text-xs font-semibold text-white hover:bg-amber-700 disabled:opacity-60"
                >
                  <BadgeCheck className="h-4 w-4" />
                  {verificationSending ? "Sending..." : "Send verification"}
                </button>
              </div>
            ) : null}

            {verificationMessage ? (
              <div className="rounded-2xl border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-800">
                {verificationMessage}
              </div>
            ) : null}

            {activeView === "profile" ? (
              <>
                <section className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3">
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <div>
                      <h3 className="text-sm font-semibold text-slate-900">My profile</h3>
                      <p className="text-xs text-slate-500">
                        Keep your contact details current for deliveries and follow-up.
                      </p>
                    </div>

                    {!isEditing ? (
                      <button
                        type="button"
                        onClick={onEditProfile}
                        className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-100"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                        Edit
                      </button>
                    ) : null}
                  </div>

                  {isEditing ? (
                    <div className="space-y-3">
                      <label className="block">
                        <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                          Full name
                        </div>
                        <input
                          type="text"
                          value={profileForm?.fullName || ""}
                          onChange={(e) =>
                            setProfileForm((prev) => ({ ...(prev || {}), fullName: e.target.value }))
                          }
                          className="h-10 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none focus:border-blue-500"
                        />
                      </label>

                      <label className="block">
                        <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                          Phone
                        </div>
                        <input
                          type="tel"
                          value={profileForm?.phone || ""}
                          onChange={(e) =>
                            setProfileForm((prev) => ({ ...(prev || {}), phone: e.target.value }))
                          }
                          className="h-10 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none focus:border-blue-500"
                        />
                      </label>

                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={onCancelProfile}
                          className="flex-1 rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={onSaveProfile}
                          disabled={savingProfile}
                          className="flex-1 rounded-xl bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
                        >
                          {savingProfile ? "Saving..." : "Save profile"}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="grid gap-2 sm:grid-cols-2">
                      <DetailRow icon={UserRound} label="Name" value={customerName} />
                      <DetailRow
                        icon={Mail}
                        label="Email"
                        value={customerEmail}
                        href={customerEmail ? `mailto:${customerEmail}` : undefined}
                      />
                      <DetailRow
                        icon={Phone}
                        label="Phone"
                        value={profileForm?.phone || "No phone added yet"}
                      />
                      <DetailRow
                        icon={Store}
                        label="Primary location"
                        value={primaryLocationName}
                      />
                    </div>
                  )}
                </section>

                <section className="rounded-2xl border border-slate-200 bg-white px-3 py-3">
                  <div className="mb-3 flex items-start justify-between gap-3">
                    <div>
                      <h3 className="text-sm font-semibold text-slate-900">Restaurants & shipping</h3>
                      <p className="text-xs text-slate-500">
                        Update the locations your team wants samples delivered to.
                      </p>
                    </div>
                    <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-600">
                      {orderedLocations.length}
                    </span>
                  </div>

                  <div className="space-y-2.5">
                    {orderedLocations.map((acc) => {
                      const id = getAccountId(acc);
                      const isPrimary = id === effectivePrimaryId;
                      const isEditingLocation = editingLocationId === id;
                      const isSavingLocation = locationSavingId === id;

                      return (
                        <div
                          key={id || acc?.id}
                          className={`rounded-2xl border px-3 py-3 ${
                            isPrimary
                              ? "border-blue-200 bg-blue-50/70"
                              : "border-slate-200 bg-slate-50/70"
                          }`}
                        >
                          {isEditingLocation ? (
                            <div className="space-y-3">
                              <div className="grid gap-3 sm:grid-cols-2">
                                <label className="block">
                                  <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                    Location name
                                  </div>
                                  <input
                                    type="text"
                                    value={locationDraft.name}
                                    onChange={(e) =>
                                      setLocationDraft((prev) => ({ ...prev, name: e.target.value }))
                                    }
                                    className="h-10 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none focus:border-blue-500"
                                  />
                                </label>

                                <label className="block">
                                  <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                    Phone
                                  </div>
                                  <input
                                    type="tel"
                                    value={locationDraft.phone}
                                    onChange={(e) =>
                                      setLocationDraft((prev) => ({ ...prev, phone: e.target.value }))
                                    }
                                    className="h-10 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none focus:border-blue-500"
                                  />
                                </label>
                              </div>

                              <label className="block">
                                <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                  Street
                                </div>
                                <input
                                  type="text"
                                  value={locationDraft.street}
                                  onChange={(e) =>
                                    setLocationDraft((prev) => ({ ...prev, street: e.target.value }))
                                  }
                                  className="h-10 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none focus:border-blue-500"
                                />
                              </label>

                              <div className="grid gap-3 sm:grid-cols-3">
                                <label className="block sm:col-span-1">
                                  <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                    City
                                  </div>
                                  <input
                                    type="text"
                                    value={locationDraft.city}
                                    onChange={(e) =>
                                      setLocationDraft((prev) => ({ ...prev, city: e.target.value }))
                                    }
                                    className="h-10 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none focus:border-blue-500"
                                  />
                                </label>
                                <label className="block">
                                  <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                    State
                                  </div>
                                  <input
                                    type="text"
                                    value={locationDraft.state}
                                    onChange={(e) =>
                                      setLocationDraft((prev) => ({ ...prev, state: e.target.value }))
                                    }
                                    className="h-10 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none focus:border-blue-500"
                                  />
                                </label>
                                <label className="block">
                                  <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                    Zip
                                  </div>
                                  <input
                                    type="text"
                                    value={locationDraft.zip}
                                    onChange={(e) =>
                                      setLocationDraft((prev) => ({ ...prev, zip: e.target.value }))
                                    }
                                    className="h-10 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none focus:border-blue-500"
                                  />
                                </label>
                              </div>

                              <div className="flex gap-2">
                                <button
                                  type="button"
                                  onClick={cancelLocationEditor}
                                  className="flex-1 rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100"
                                >
                                  Cancel
                                </button>
                                <button
                                  type="button"
                                  onClick={handleLocationSave}
                                  disabled={
                                    isSavingLocation ||
                                    !locationDraft.name.trim() ||
                                    !locationDraft.street.trim() ||
                                    !locationDraft.city.trim() ||
                                    !locationDraft.state.trim()
                                  }
                                  className="flex-1 rounded-xl bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
                                >
                                  {isSavingLocation ? "Saving..." : "Save location"}
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0 flex-1">
                                <div className="flex flex-wrap items-center gap-2">
                                  <p className="text-sm font-semibold text-slate-900">
                                    {getAccountDisplayName(acc)}
                                  </p>
                                  {isPrimary ? (
                                    <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-[11px] font-semibold text-blue-700">
                                      <Star className="h-3 w-3" />
                                      Primary
                                    </span>
                                  ) : null}
                                </div>

                                <div className="mt-2 grid gap-2 sm:grid-cols-2">
                                  <DetailRow icon={MapPin} label="Address" value={formatAddress(acc)} />
                                  <DetailRow
                                    icon={Phone}
                                    label="Phone"
                                    value={acc?.phone || acc?.primaryContact?.phone || "No phone added"}
                                  />
                                </div>
                              </div>

                              <div className="flex shrink-0 flex-col items-end gap-2">
                                <button
                                  type="button"
                                  onClick={() => launchLocationEditor(acc)}
                                  className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-100"
                                >
                                  <Pencil className="h-3.5 w-3.5" />
                                  Edit
                                </button>
                                {!isPrimary && typeof onSetPrimaryLocation === "function" ? (
                                  <button
                                    type="button"
                                    disabled={settingPrimary}
                                    onClick={() => onSetPrimaryLocation(id)}
                                    className="rounded-xl border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-700 hover:bg-blue-100 disabled:opacity-60"
                                  >
                                    {settingPrimary ? "Saving..." : "Make primary"}
                                  </button>
                                ) : null}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  <div className="mt-3 rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-3 py-3">
                    <button
                      type="button"
                      onClick={() => setShowAddLocation((prev) => !prev)}
                      className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700 hover:text-blue-700"
                    >
                      {showAddLocation ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                      {showAddLocation ? "Cancel new location" : "Add another location"}
                    </button>

                    {showAddLocation ? (
                      <div className="mt-3 space-y-3 max-h-[70vh] overflow-y-auto overscroll-contain pr-1 sm:max-h-none sm:overflow-visible sm:pr-0">
                        <div className="grid gap-3 sm:grid-cols-2">
                          <label className="block">
                            <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                              Location name
                            </div>
                            <input
                              type="text"
                              value={newLocation.name}
                              onChange={(e) =>
                                setNewLocation((prev) => ({ ...prev, name: e.target.value }))
                              }
                              className="h-10 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none focus:border-blue-500"
                            />
                          </label>

                          <label className="block">
                            <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                              Phone
                            </div>
                            <input
                              type="tel"
                              value={newLocation.phone}
                              onChange={(e) =>
                                setNewLocation((prev) => ({ ...prev, phone: e.target.value }))
                              }
                              className="h-10 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none focus:border-blue-500"
                            />
                          </label>
                        </div>

                        <label className="block">
                          <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                            Street
                          </div>
                          <input
                            type="text"
                            value={newLocation.street}
                            onChange={(e) =>
                              setNewLocation((prev) => ({ ...prev, street: e.target.value }))
                            }
                            className="h-10 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none focus:border-blue-500"
                          />
                        </label>

                        <div className="grid gap-3 sm:grid-cols-3">
                          <label className="block">
                            <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                              City
                            </div>
                            <input
                              type="text"
                              value={newLocation.city}
                              onChange={(e) =>
                                setNewLocation((prev) => ({ ...prev, city: e.target.value }))
                              }
                              className="h-10 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none focus:border-blue-500"
                            />
                          </label>
                          <label className="block">
                            <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                              State
                            </div>
                            <input
                              type="text"
                              value={newLocation.state}
                              onChange={(e) =>
                                setNewLocation((prev) => ({ ...prev, state: e.target.value }))
                              }
                              className="h-10 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none focus:border-blue-500"
                            />
                          </label>
                          <label className="block">
                            <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                              Zip
                            </div>
                            <input
                              type="text"
                              value={newLocation.zip}
                              onChange={(e) =>
                                setNewLocation((prev) => ({ ...prev, zip: e.target.value }))
                              }
                              className="h-10 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 outline-none focus:border-blue-500"
                            />
                          </label>
                        </div>

                        <button
                          type="button"
                          onClick={() => onAddLocationSubmit()}
                          disabled={
                            addLocationSaving ||
                            !newLocation.name.trim() ||
                            !newLocation.street.trim() ||
                            !newLocation.city.trim() ||
                            !newLocation.state.trim()
                          }
                          className="w-full rounded-xl bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
                        >
                          {addLocationSaving ? "Saving..." : "Save new location"}
                        </button>
                      </div>
                    ) : null}
                  </div>
                </section>

              </>
            ) : (
              <>
                <section className="rounded-2xl bg-gradient-to-br from-blue-50 to-white px-3 py-3 ring-1 ring-inset ring-blue-100">
                  <div className="flex items-start gap-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-600 text-white">
                      <Building2 className="h-5 w-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="text-base font-semibold text-slate-900">{companyName}</h3>
                      <p className="mt-1 text-sm text-slate-600">{companyDescription}</p>
                    </div>
                  </div>

                  <div className="mt-4 grid gap-2 sm:grid-cols-2">
                    <DetailRow
                      icon={Mail}
                      label="Support email"
                      value={company?.supportEmail || company?.email}
                      href={company?.supportEmail || company?.email ? `mailto:${company?.supportEmail || company?.email}` : undefined}
                    />
                    <DetailRow
                      icon={Phone}
                      label="Phone"
                      value={company?.phone || company?.officePhone}
                      href={company?.phone || company?.officePhone ? `tel:${company?.phone || company?.officePhone}` : undefined}
                    />
                    <DetailRow
                      icon={MapPin}
                      label="Headquarters"
                      value={[
                        company?.mailingAddress?.street,
                        [company?.mailingAddress?.city, company?.mailingAddress?.state].filter(Boolean).join(", "),
                        company?.mailingAddress?.zip,
                      ]
                        .filter(Boolean)
                        .join(" • ")}
                    />
                    <DetailRow
                      icon={Building2}
                      label="Website"
                      value={company?.website || company?.socialLinks?.website}
                      href={company?.website || company?.socialLinks?.website}
                    />
                  </div>
                </section>

                <section className="rounded-2xl bg-white px-3 py-3 ring-1 ring-inset ring-slate-200/80">
                  <div className="flex items-start gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-600 text-sm font-semibold text-white shadow-sm">
                      {repInitial || "?"}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="text-sm font-semibold text-slate-900">Assigned contact</h3>
                        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                          Support
                        </span>
                      </div>
                      <p className="mt-1 text-xs text-slate-500">
                        Your day-to-day rep for samples, follow-up, and account support.
                      </p>
                      <p className="text-sm font-semibold text-slate-900">
                        {displayRep?.fullName || displayRep?.displayName || "Your rep will be assigned soon"}
                      </p>
                      {displayRep?.email ? (
                        <a
                          href={`mailto:${displayRep.email}`}
                          className="mt-1 block text-sm text-blue-700 hover:underline"
                        >
                          {displayRep.email}
                        </a>
                      ) : null}
                      {displayRep?.phone ? (
                        <a
                          href={`tel:${displayRep.phone}`}
                          className="mt-1 block text-sm text-slate-600 hover:text-slate-900"
                        >
                          {displayRep.phone}
                        </a>
                      ) : null}
                      {!displayRep?.phone && !displayRep?.email && !displayRep?.fullName ? (
                        <p className="mt-1 text-sm text-slate-500">
                          Your distributor will connect you with the right rep.
                        </p>
                      ) : null}
                    </div>
                  </div>
                </section>

                <section className="rounded-2xl bg-white px-3 py-3 ring-1 ring-inset ring-slate-200/80">
                  <div className="flex items-start gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-slate-100 text-slate-600">
                      <MapPin className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="text-sm font-semibold text-slate-900">Primary delivery location</h3>
                      <p className="mt-1 text-sm font-semibold text-slate-900">{primaryLocationName}</p>
                      <p className="mt-1 text-xs text-slate-600">{formatAddress(primaryLocation)}</p>
                    </div>
                  </div>
                </section>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerInfoPanel;
